import { useEffect } from 'react';
import tomorrowIoTilesService from '../../api/tomorrowIoTilesService';

// ============================================================================
// Types
// ============================================================================

export interface DateRange {
  start: Date;
  end: Date;
}

export interface UseTioTimestampsParams {
  selectedDateRange: DateRange | null;
  setTioTimestamps: React.Dispatch<React.SetStateAction<string[]>>;
  setTioCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
  tioCurrentIndexRef: React.RefObject<number>;
  previousTioTimestampsRef: React.RefObject<string[]>;
  previousDateRangeRef: React.RefObject<{ start: Date; end: Date } | null>;
  /** When false, skips timestamp generation (event ended >14 days ago). Default true. */
  enabled?: boolean;
  /** When true and not looping, on refresh we dock to first or last based on playbackDock */
  isEventActive?: boolean;
  autoLoop?: boolean;
  /** When not looping in live mode: dock to 'first', 'current', 'now', or 'last' tile on refresh */
  playbackDock?: 'first' | 'current' | 'now' | 'last';
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to generate Tomorrow.io timestamps when date range changes.
 * 
 * Responsibilities:
 * - Generate timestamps with variable intervals (high-res for first 2 hours, low-res after)
 * - Preserve playback position on refresh by finding closest timestamp
 * - Clear timestamps when no date range selected
 * - Update refs for position preservation across refreshes
 */
export function useTioTimestamps({
  selectedDateRange,
  setTioTimestamps,
  setTioCurrentIndex,
  tioCurrentIndexRef,
  previousTioTimestampsRef,
  previousDateRangeRef,
  enabled = true,
  isEventActive = false,
  autoLoop = true,
  playbackDock = 'last',
}: UseTioTimestampsParams): void {
  useEffect(() => {
    if (!enabled) {
      setTioTimestamps([]);
      setTioCurrentIndex(0);
      previousTioTimestampsRef.current = [];
      previousDateRangeRef.current = null;
      return;
    }

    if (!selectedDateRange) {
      setTioTimestamps([]);
      setTioCurrentIndex(0);
      previousTioTimestampsRef.current = [];
      previousDateRangeRef.current = null;
      return;
    }

    // Check if this is a refresh (we have previous timestamps to try to preserve position)
    const isRefresh = previousTioTimestampsRef.current.length > 0 && 
                      previousDateRangeRef.current !== null;

    // In live mode without auto-loop, dock based on user preference
    const shouldDockOnRefresh = isEventActive && !autoLoop && isRefresh;
    const dockToLast = playbackDock === 'last';
    const dockToNow = playbackDock === 'now';
    const dockToCurrent = playbackDock === 'current';

    // Get the current timestamp before regenerating (to preserve playback position)
    // Use the ref to get current index to avoid dependency on tioCurrentIndex state
    const currentIdx = tioCurrentIndexRef.current;
    const currentTimestamp = isRefresh && (dockToCurrent || !shouldDockOnRefresh) && currentIdx < previousTioTimestampsRef.current.length
      ? previousTioTimestampsRef.current[currentIdx]
      : null;

    // Always regenerate timestamps fresh - don't merge to avoid accumulation
    // Variable intervals are based on current time, so regenerating ensures correct intervals
    // First 2 hours from now: 10-minute intervals (high resolution)
    // After 2 hours: 60-minute intervals (matches Tomorrow.io model update frequency)
    const timestamps = tomorrowIoTilesService.generateTimestamps(
      selectedDateRange.start,
      selectedDateRange.end,
      {
        useVariableIntervals: true,
        highResIntervalMinutes: 10,
        lowResIntervalMinutes: 60,
        highResHours: 2,
      }
    );

    // Update refs for next refresh
    previousTioTimestampsRef.current = timestamps;
    previousDateRangeRef.current = { 
      start: new Date(selectedDateRange.start), 
      end: new Date(selectedDateRange.end) 
    };

    setTioTimestamps(timestamps);
    
    // On refresh: dock to first, last, now, or stay on current (closest match)
    if (shouldDockOnRefresh && timestamps.length > 0) {
      if (dockToLast) {
        const dockIndex = timestamps.length - 1;
        setTioCurrentIndex(dockIndex);
        tioCurrentIndexRef.current = dockIndex;
      } else if (dockToNow) {
        const { index } = findClosestTimestampIndexOrOutOfRange(timestamps, new Date().toISOString());
        setTioCurrentIndex(index);
        tioCurrentIndexRef.current = index;
      } else if (dockToCurrent && currentTimestamp) {
        // Stay on same tile: find closest. If pushed out of range, fall back to first
        const { index, isOutOfRange } = findClosestTimestampIndexOrOutOfRange(timestamps, currentTimestamp);
        setTioCurrentIndex(isOutOfRange ? 0 : index);
        tioCurrentIndexRef.current = isOutOfRange ? 0 : index;
      } else {
        setTioCurrentIndex(0);
        tioCurrentIndexRef.current = 0;
      }
    } else if (isRefresh && currentTimestamp) {
      const { index, isOutOfRange } = findClosestTimestampIndexOrOutOfRange(timestamps, currentTimestamp);
      setTioCurrentIndex(isOutOfRange ? 0 : index);
      tioCurrentIndexRef.current = isOutOfRange ? 0 : index;
    } else {
      setTioCurrentIndex(0);
      tioCurrentIndexRef.current = 0;
    }
  }, [enabled, selectedDateRange, tioCurrentIndexRef, previousDateRangeRef, previousTioTimestampsRef, setTioCurrentIndex, setTioTimestamps, isEventActive, autoLoop, playbackDock]);
}

// ============================================================================
// Helper Functions
// ============================================================================

/** Max distance (ms) to consider tile "in range" - ~15 min. Beyond this, fall back to first. */
const OUT_OF_RANGE_THRESHOLD_MS = 15 * 60 * 1000;

/**
 * Find the index of the timestamp closest to a target timestamp.
 * Returns outOfRange: true if closest is beyond threshold (tile pushed out of selected range).
 */
function findClosestTimestampIndexOrOutOfRange(
  timestamps: string[],
  targetTimestamp: string
): { index: number; isOutOfRange: boolean } {
  const targetTime = new Date(targetTimestamp).getTime();
  let closestIndex = 0;
  let closestDiff = Infinity;

  timestamps.forEach((ts, idx) => {
    const diff = Math.abs(new Date(ts).getTime() - targetTime);
    if (diff < closestDiff) {
      closestDiff = diff;
      closestIndex = idx;
    }
  });

  const isOutOfRange = closestDiff > OUT_OF_RANGE_THRESHOLD_MS;
  return { index: closestIndex, isOutOfRange };
}
